import { useState } from 'react';
import { APIProvider, Map } from '@vis.gl/react-google-maps';

import { Button } from 'modules/v2/common/AtomicDesign/atoms';
import AddressGoogleLookup from 'modules/v2/common/components/AddressGoogleLookup';
import { regexUSA } from 'modules/editor/components/AddressForm/validationSchema';
import env from 'environments';

import strings, { formatMessage } from '../../strings';

const formatOriginal = (original) => {
  if (!original) {
    return null;
  }
  return [original.address1, original.city, original.state, original.zip]
    .filter(Boolean)
    .join(', ');
};

// AddressGoogleLookup emits Welcome-Journey field names; MOD uses a different
// shape (and a 2-letter ISO country). Map names + normalize the country code so
// the picked address lands in the modal's form state.
const COUNTRY_TO_ISO = { USA: 'US', US: 'US', Canada: 'CA', CA: 'CA' };

// MOD shows autocomplete only (no map), so the place/geocoder callbacks the
// lookup expects are intentionally unused here.
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const mapGoogleField = (googleField, value) => {
  switch (googleField) {
    case 'addressLine1':
      return { address1: value };
    case 'pincode':
      return { zip: value };
    case 'country':
      return { country: COUNTRY_TO_ISO[value] || value };
    case 'city':
    case 'state':
      return { [googleField]: value };
    default:
      return null;
  }
};

// MOD-local Canadian postal-code pattern. The shared editor `regexCanada`
// requires a literal `●` between the two triplets, so it rejects the spaced
// form Google autocomplete returns ("K1A 0B1") and over-blocks Canadian
// members at Save (task 52). This accepts the spaced, hyphen, and no-space
// forms, case-insensitively, WITHOUT touching the shared regex (it has other
// consumers in the editor AddressForm).
const regexCanadaPostal = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

// Resolve the country the ZIP should be validated/saved against. An explicit
// US/CA country always wins. When country is empty (e.g. the manual-entry
// path), infer CA from a CA-format postal code so a manually-typed Canadian
// code is validated against CA rules — a genuine US ZIP can't match the CA
// pattern, so US-or-empty still falls back to the US rule.
const resolveCountry = (zip, country) => {
  const iso = (country || '').trim().toUpperCase();
  if (iso === 'US' || iso === 'CA') {
    return iso;
  }
  if (iso === '' && regexCanadaPostal.test((zip || '').trim())) {
    return 'CA';
  }
  return iso;
};

// ZIP must match the selected country (US: 5/9-digit, CA: postal code).
const isZipValid = (zip, country) => {
  const value = (zip || '').trim();
  if (!value) {
    return false;
  }
  const iso = resolveCountry(value, country);
  if (iso === 'CA') {
    return regexCanadaPostal.test(value);
  }
  if (iso === 'US' || iso === '') {
    return regexUSA.test(value);
  }
  // Unknown / non-US/CA country: accept any non-empty value (US+CA only is
  // enforced by the Google predictions; this is a permissive manual fallback).
  return true;
};

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="text-xs font-semibold text-neutral-600">{label}</label>
    {children}
  </div>
);

/**
 * Per-order address override (ADR-006 / task 31). Edits this mailing only —
 * never writes back to Marketing Hub. On save the parent recounts totals
 * from the server response.
 *
 * Street address uses the shared Google Places `AddressGoogleLookup` (task 44),
 * reusing afy-ui's existing integration (`@vis.gl/react-google-maps` +
 * `env.GOOGLE_MAP_API_KEY`) with the Leads hidden-map pattern. A manual-entry
 * fallback stays available for corrections and unsupported addresses.
 */
const EditAddressModal = ({ recipient, onSave, onClose, isSaving, error }) => {
  const [form, setForm] = useState({
    address1: recipient.address1 || '',
    address2: recipient.address2 || '',
    city: recipient.city || '',
    state: recipient.state || '',
    zip: recipient.zip || '',
    country: recipient.country || '',
  });
  // Start in manual mode when the recipient already has a street address so the
  // member can correct it directly; otherwise default to the Google lookup.
  const [isManualEntry, setIsManualEntry] = useState(
    Boolean(recipient.address1),
  );

  const setField = (key) => (event) => {
    // Read the value before the functional updater runs so a stale-closure
    // capture can't drop fields when several change before the next render.
    const { value } = event.target;
    setForm((current) => ({ ...current, [key]: value }));
  };

  // Shim passed to AddressGoogleLookup: translate its field names into the MOD
  // shape and fold each update into the modal's form state.
  const setFieldValue = (googleField, value) => {
    const mapped = mapGoogleField(googleField, value);
    if (!mapped) {
      return;
    }
    setForm((current) => ({ ...current, ...mapped }));
  };

  const zipValid = isZipValid(form.zip, form.country);
  const canSave =
    form.address1.trim() &&
    form.city.trim() &&
    form.state.trim() &&
    zipValid &&
    !isSaving;
  const showZipError = form.zip.trim() && !zipValid;

  const original = formatOriginal(recipient.originalAddress);
  const name = `${recipient.firstName || ''} ${recipient.lastName || ''}`.trim();

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-neutral-900/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl" data-testid="mod-edit-address-modal">
        <h3 className="text-lg font-semibold">
          {formatMessage(strings.editAddress.title, { name })}
        </h3>
        <p className="mt-0.5 text-xs text-neutral-500">{strings.editAddress.subtitle}</p>

        <div className="mt-3 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-500">
          {strings.editAddress.fromHub}{' '}
          {original || (
            <span className="italic">{strings.editAddress.fromHubEmpty}</span>
          )}
        </div>

        <div className="mt-4 space-y-3 text-sm">
          <APIProvider apiKey={env.GOOGLE_MAP_API_KEY} libraries={['marker']}>
            {/* Hidden map: AddressGoogleLookup needs a Map instance for the
                PlacesService, but MOD shows autocomplete only (no map). */}
            <div className="hidden">
              <Map
                mapId="mod-edit-address"
                style={{ width: '100%', height: 0 }}
                defaultZoom={15}
                defaultCenter={{ lat: 0, lng: 0 }}
                gestureHandling="greedy"
                disableDefaultUI
              />
            </div>

            {isManualEntry ? (
              <Field label={`${strings.editAddress.address1} *`}>
                <input
                  className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2"
                  value={form.address1}
                  onChange={setField('address1')}
                  data-testid="mod-edit-address1"
                />
                <button
                  type="button"
                  className="mt-1 text-xs font-semibold text-primary-700"
                  onClick={() => setIsManualEntry(false)}
                  data-testid="mod-edit-use-lookup"
                >
                  {strings.editAddress.useLookup}
                </button>
              </Field>
            ) : (
              <div data-testid="mod-edit-address-lookup">
                <AddressGoogleLookup
                  setSelectedPlace={noop}
                  setGeocoderLocation={noop}
                  setFieldValue={setFieldValue}
                  setIsManualEntry={setIsManualEntry}
                  required
                  initialInputValue={form.address1}
                />
              </div>
            )}
          </APIProvider>

          <Field label={strings.editAddress.address2}>
            <input
              className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2"
              value={form.address2}
              onChange={setField('address2')}
              placeholder={strings.editAddress.optional}
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label={`${strings.editAddress.city} *`} className="col-span-1">
              <input
                className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2"
                value={form.city}
                onChange={setField('city')}
                data-testid="mod-edit-city"
              />
            </Field>
            <Field label={`${strings.editAddress.state} *`}>
              <input
                className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2"
                value={form.state}
                onChange={setField('state')}
                data-testid="mod-edit-state"
              />
            </Field>
            <Field label={`${strings.editAddress.zip} *`}>
              <input
                className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2"
                value={form.zip}
                onChange={setField('zip')}
                data-testid="mod-edit-zip"
              />
            </Field>
          </div>
          {showZipError && (
            <p className="text-xs text-error-500" data-testid="mod-edit-zip-error">
              {strings.editAddress.zipInvalid}
            </p>
          )}
        </div>

        <div className="mt-4 rounded-md border border-primary-100 bg-primary-50 px-3 py-2 text-xs text-primary-700">
          {strings.editAddress.nudge}
        </div>

        {error && (
          <p className="mt-3 text-sm text-error-500" data-testid="mod-edit-address-error">
            {strings.editAddress.saveError}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <Button color="outlined" onClick={onClose} disabled={isSaving}>
            {strings.editAddress.cancel}
          </Button>
          <Button
            onClick={() =>
              onSave({
                address1: form.address1.trim(),
                address2: form.address2.trim() || undefined,
                city: form.city.trim(),
                state: form.state.trim(),
                zip: form.zip.trim(),
                // Persist the resolved country so a manually-typed CA postal
                // code saves as CA (per-order override, ADR-006) instead of
                // an empty/US country.
                country:
                  resolveCountry(form.zip, form.country) || undefined,
              })
            }
            disabled={!canSave}
            data-testid="mod-edit-address-save"
          >
            {isSaving ? strings.editAddress.saving : strings.editAddress.save}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditAddressModal;
